"use client";

import { Fragment, type KeyboardEvent, type ReactNode, useEffect, useId, useRef, useState } from "react";
import { AltArrowDownLinear } from "solar-icon-set";
import { cn } from "../../lib/utils";
import { Chip } from "../Chip/Chip";
import { Spinner } from "../Spinner/Spinner";
import { Tooltip } from "../Tooltip/Tooltip";
import { useUiLabels } from "../UiLabels/useUiLabels";

/** A titled group of options, rendered as a labelled section in the dropdown. */
export interface MultiSelectComboboxSection<T> {
  /** Stable identity for the section — used as the React key. */
  key: string;
  title: ReactNode;
  items: T[];
}

export interface MultiSelectComboboxProps<T> {
  /** All candidate options. Already-selected items are filtered out automatically via `getKey`. */
  items: T[];
  /**
   * Optional grouped options. When provided, the dropdown renders titled
   * sections (in order) instead of the flat `items` list; `items` is ignored.
   * Already-selected items are filtered out of each section, and empty sections
   * are dropped.
   */
  sections?: MultiSelectComboboxSection<T>[];
  /** Currently selected items, rendered as removable chips inside the field. */
  selectedItems: T[];
  /** Stable identity for an item — used as the React key and to dedupe selected vs. available. */
  getKey: (item: T) => string;
  /** Plain-text label for an item — used as the chip text and option fallback. */
  getTextValue: (item: T) => string;
  onAdd: (item: T) => void;
  onRemove: (item: T) => void;
  /** Controlled search text for the typeahead input. */
  inputValue: string;
  onInputChange: (value: string) => void;
  /** Leading content inside each chip (e.g. an avatar). */
  renderChipStartContent?: (item: T) => ReactNode;
  /** Full custom render for a dropdown option. Defaults to the item's text value. */
  renderOption?: (item: T) => ReactNode;
  /**
   * Tooltip for a selected chip — for a chip that stands for several things, say, where the label
   * alone doesn't tell the reader who or what it covers. Return null for no tooltip.
   */
  getChipTooltip?: (item: T) => ReactNode;
  placeholder?: string;
  ariaLabel?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  /** Marks the field as failing validation. The message itself is the host's to render. */
  isInvalid?: boolean;
  emptyContent?: ReactNode;
  /**
   * Which side of the field the dropdown opens on. Hosts nest this field inside `overflow`
   * containers (panel bodies, modal bodies) that clip the list, and no z-index undoes that — so
   * the side is the host's call. Defaults to "bottom"; pass "top" where the field sits low enough
   * in its container that a downward list would open off the end of it.
   */
  placement?: "top" | "bottom";
  /** A distinct action shown at the top of the dropdown (e.g. a "defer choice" option). */
  leadingOption?: {
    label: ReactNode;
    description?: ReactNode;
    icon?: ReactNode;
    onSelect: () => void;
  };
  className?: string;
}

/**
 * A token-style multi-select combobox: selected items render as removable chips
 * *inside* a single bordered field, followed by an inline typeahead input. Chips
 * and the input share one flex-wrap row, so they spread horizontally and wrap to
 * new lines as the selection grows — the field grows with them.
 *
 * Options can be a flat `items` list or grouped `sections` (titled groups). Either
 * way selected items are filtered out and the remaining options form a single flat
 * navigation order.
 *
 * Keyboard: type to filter; ArrowDown/ArrowUp move the active option (with wrap),
 * Home/End jump to first/last, Enter selects the active option, Escape closes, and
 * Backspace on an empty input removes the last chip.
 *
 * Controlled: the consumer owns `selectedItems` and the search `inputValue`, and
 * feeds the (already searched/paged) options to show. Generic over the item type.
 */
export function MultiSelectCombobox<T>({
  items,
  sections,
  selectedItems,
  getKey,
  getTextValue,
  onAdd,
  onRemove,
  inputValue,
  onInputChange,
  renderChipStartContent,
  renderOption,
  getChipTooltip,
  placeholder,
  ariaLabel,
  isLoading,
  isDisabled,
  isInvalid,
  emptyContent = "No results",
  placement = "bottom",
  leadingOption,
  className,
}: MultiSelectComboboxProps<T>) {
  const uiLabels = useUiLabels();
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const optionId = (index: number) => `${baseId}-option-${index}`;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  // Index into the flat list of navigable entries (leading option first, then options).
  const [activeIndex, setActiveIndex] = useState(-1);

  const selectedKeys = new Set(selectedItems.map(getKey));
  const isAvailable = (item: T) => !selectedKeys.has(getKey(item));
  // Drop selected items (and, in grouped mode, any section left empty) so the
  // rendered options and the flat navigation order stay in lock-step.
  const availableSections = sections
    ?.map((section) => ({ ...section, items: section.items.filter(isAvailable) }))
    .filter((section) => section.items.length > 0);
  const availableItems = availableSections
    ? availableSections.flatMap((section) => section.items)
    : items.filter(isAvailable);
  // Options aren't rendered while loading, so they aren't navigable then.
  const optionItems = isLoading ? [] : availableItems;
  const leadingCount = leadingOption ? 1 : 0;
  const entryCount = leadingCount + optionItems.length;
  const activeId = activeIndex >= 0 && activeIndex < entryCount ? optionId(activeIndex) : undefined;
  // Flat navigation index for a rendered option, shared by flat and grouped layouts.
  const entryIndexByKey = new Map(optionItems.map((item, index) => [getKey(item), leadingCount + index]));

  useEffect(() => {
    if (!isOpen) {
      setActiveIndex(-1);
      return;
    }
    const handlePointerDown = (event: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) setIsOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isOpen]);

  // Keep the active option scrolled into view as it moves.
  useEffect(() => {
    if (!isOpen || activeIndex < 0) return;
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: "nearest" });
  }, [isOpen, activeIndex]);

  const handleAdd = (item: T) => {
    onAdd(item);
    inputRef.current?.focus();
  };

  const selectEntry = (index: number) => {
    if (index < 0 || index >= entryCount) return;
    if (leadingOption && index === 0) {
      leadingOption.onSelect();
      setIsOpen(false);
      return;
    }
    const item = optionItems[index - leadingCount];
    if (item) handleAdd(item);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setActiveIndex(0);
        } else if (entryCount > 0) {
          setActiveIndex((index) => (index + 1) % entryCount);
        }
        return;
      case "ArrowUp":
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setActiveIndex(entryCount - 1);
        } else if (entryCount > 0) {
          setActiveIndex((index) => (index - 1 + entryCount) % entryCount);
        }
        return;
      case "Home":
        if (isOpen && entryCount > 0) {
          event.preventDefault();
          setActiveIndex(0);
        }
        return;
      case "End":
        if (isOpen && entryCount > 0) {
          event.preventDefault();
          setActiveIndex(entryCount - 1);
        }
        return;
      case "Enter":
        if (isOpen && activeIndex >= 0) {
          event.preventDefault();
          selectEntry(activeIndex);
        }
        return;
      case "Escape":
        setIsOpen(false);
        return;
      case "Backspace":
        // Backspace on an empty input removes the last chip — standard token-field behaviour.
        if (inputValue === "" && selectedItems.length > 0) onRemove(selectedItems[selectedItems.length - 1]);
        return;
      default:
        return;
    }
  };

  const renderOptionButton = (item: T) => {
    const entryIndex = entryIndexByKey.get(getKey(item)) ?? -1;
    const isActive = activeIndex === entryIndex;
    return (
      <button
        key={getKey(item)}
        type="button"
        id={optionId(entryIndex)}
        role="option"
        aria-selected={isActive}
        data-active={isActive}
        className={cn(
          "flex w-full min-w-0 cursor-pointer items-center rounded-small px-2 py-1.5 text-left text-sm hover:bg-default",
          isActive && "bg-default",
        )}
        // Keep focus on the input so the dropdown's blur-close doesn't fire mid-click.
        onMouseDown={(event) => event.preventDefault()}
        onMouseEnter={() => setActiveIndex(entryIndex)}
        onClick={() => handleAdd(item)}
      >
        <div className="min-w-0 flex-1 truncate">{renderOption ? renderOption(item) : getTextValue(item)}</div>
      </button>
    );
  };

  return (
    <div ref={containerRef} className={cn("relative w-full min-w-0", className)}>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: forwards focus to the input, which handles all keyboard interaction. */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: focus-forwarding wrapper around the keyboard-accessible input. */}
      <div
        className={cn(
          // Match the app's bordered Input/Select: 1px neutral border, no shadow, across rest/hover/focus.
          "flex min-h-10 w-full min-w-0 flex-wrap items-center gap-1.5 rounded-small border-1 border-border-interactive-neutral-default bg-content1 px-2 py-1 shadow-none transition-colors",
          isOpen ? "border-border-interactive-neutral-selected" : "hover:border-border-interactive-neutral-hover",
          isInvalid && "border-danger hover:border-danger",
          isDisabled && "pointer-events-none opacity-disabled",
        )}
        aria-invalid={isInvalid || undefined}
        onClick={() => inputRef.current?.focus()}
      >
        {selectedItems.map((item) => {
          const chip = (
            <Chip
              variant="flat"
              size="sm"
              className="min-w-0 max-w-full"
              onClose={() => onRemove(item)}
              startContent={renderChipStartContent?.(item)}
            >
              <span className="block min-w-0 truncate">{getTextValue(item)}</span>
            </Chip>
          );
          const tooltip = getChipTooltip?.(item);
          return tooltip ? (
            <Tooltip key={getKey(item)} content={tooltip} placement="bottom" color="foreground">
              <span className="inline-flex min-w-0 max-w-full">{chip}</span>
            </Tooltip>
          ) : (
            <Fragment key={getKey(item)}>{chip}</Fragment>
          );
        })}
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-activedescendant={activeId}
          aria-label={ariaLabel}
          disabled={isDisabled}
          value={inputValue}
          placeholder={placeholder}
          // 16px below `sm` or iOS Safari zooms the page on focus — same guard as `Input`.
          className="min-w-0 flex-1 basis-24 truncate bg-transparent text-base text-foreground outline-none placeholder:text-text-interactive-neutral-faint sm:min-w-24 sm:text-sm"
          onChange={(event) => {
            onInputChange(event.target.value);
            setIsOpen(true);
            setActiveIndex(-1);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          // Not in the tab order — the input is the control; this is a pointer affordance for the dropdown.
          tabIndex={-1}
          aria-label={isOpen ? uiLabels.multiSelect.hideOptions : uiLabels.multiSelect.showOptions}
          disabled={isDisabled}
          className="shrink-0 self-center px-1 text-neutral-onMatch-muted"
          // Toggle without stealing focus from the input.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            setIsOpen((open) => !open);
            inputRef.current?.focus();
          }}
        >
          <AltArrowDownLinear className={cn("size-3.5 transition-transform", isOpen && "rotate-180")} />
        </button>
      </div>

      {isOpen ? (
        <div
          ref={listRef}
          id={listboxId}
          aria-label={ariaLabel}
          role="listbox"
          className={cn(
            "absolute inset-x-0 z-50 max-h-64 min-w-0 overflow-auto overflow-x-hidden overscroll-contain rounded-medium border border-default-200 bg-content1 p-1 shadow-medium",
            placement === "top" ? "bottom-[calc(100%+0.25rem)]" : "top-[calc(100%+0.25rem)]",
          )}
        >
          {leadingOption ? (
            <>
              <button
                type="button"
                id={optionId(0)}
                role="option"
                aria-selected={activeIndex === 0}
                data-active={activeIndex === 0}
                className={cn(
                  "flex w-full min-w-0 cursor-pointer items-center gap-2 rounded-small px-2 py-1.5 text-left hover:bg-default",
                  activeIndex === 0 && "bg-default",
                )}
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setActiveIndex(0)}
                onClick={() => {
                  leadingOption.onSelect();
                  setIsOpen(false);
                }}
              >
                {leadingOption.icon}
                <span className="flex min-w-0 flex-col leading-tight">
                  <span className="truncate font-semibold text-foreground text-sm">{leadingOption.label}</span>
                  {leadingOption.description ? (
                    <span className="truncate text-text-interactive-neutral-faint text-xs">
                      {leadingOption.description}
                    </span>
                  ) : null}
                </span>
              </button>
              <div className="my-1 border-divider border-t" />
            </>
          ) : null}
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 px-2 py-3 text-default-500 text-sm">
              <Spinner size="sm" /> {uiLabels.multiSelect.loading}
            </div>
          ) : optionItems.length === 0 ? (
            <div className="px-2 py-3 text-center text-default-400 text-sm">{emptyContent}</div>
          ) : availableSections ? (
            availableSections.map((section) => (
              // biome-ignore lint/a11y/useSemanticElements: role="group" is the correct ARIA grouping inside role="listbox"; a <fieldset> would break the listbox/option structure
              <div
                key={section.key}
                role="group"
                aria-label={typeof section.title === "string" ? section.title : undefined}
              >
                <div className="px-2 pt-2 pb-1 font-medium text-default-500 text-xs">{section.title}</div>
                {section.items.map(renderOptionButton)}
              </div>
            ))
          ) : (
            optionItems.map(renderOptionButton)
          )}
        </div>
      ) : null}
    </div>
  );
}
